import type { ExplainerCtaConfig } from '@arthelper/shared/feature-wizard-config';
import type { StepDraft, WizardDraft, WizardRecord, WizardRegistryEntry } from './types';
import { validHostedUrl } from '../../../utils/url';

/** Lowercase kebab-case, the same pattern the API enforces before it will store a slug. */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Half the audience is the most that may be held out. */
const MAX_CONTROL_PERCENT = 50;

let keySeq = 0;

/** Form-local identity for a step row, stable across renumbering. */
export function nextStepKey(): string {
  keySeq += 1;
  return `step-${keySeq}`;
}

export function fieldText(config: Record<string, unknown>, key: string): string {
  const value = config[key];
  return typeof value === 'string' ? value : '';
}

/** Positions must run 1..n with no gaps, which is exactly what the API validates. */
export function renumber(steps: StepDraft[]): StepDraft[] {
  return steps.map((step, index) => ({ ...step, position: index + 1 }));
}

export function blankStep(entry: WizardRegistryEntry, position: number): StepDraft {
  return { key: nextStepKey(), stepCode: entry.code, position, config: { ...entry.defaultConfig } };
}

export function blankDraft(entry: WizardRegistryEntry): WizardDraft {
  return {
    id: null,
    slug: '',
    name: '',
    description: '',
    controlPercent: '0',
    targetEventName: '',
    handoffHref: '',
    status: 'DRAFT',
    publishedAt: null,
    steps: [blankStep(entry, 1)],
  };
}

export function draftFromWizard(wizard: WizardRecord): WizardDraft {
  return {
    id: wizard.id,
    slug: wizard.slug,
    name: wizard.name,
    description: wizard.description ?? '',
    controlPercent: String(wizard.controlPercent),
    targetEventName: wizard.targetEventName,
    handoffHref: wizard.handoffHref ?? '',
    status: wizard.status,
    publishedAt: wizard.publishedAt,
    steps: [...wizard.steps]
      .sort((a, b) => a.position - b.position)
      .map((step, index) => ({
        key: nextStepKey(),
        stepCode: step.stepCode,
        position: index + 1,
        config: { ...step.config },
      })),
  };
}

/**
 * The shape the shared step renderer takes. Blanks become absent rather than empty strings
 * so a step with no image renders exactly as a wizard with no images anywhere does.
 */
export function toStepConfig(config: Record<string, unknown>): ExplainerCtaConfig {
  const imageUrl = fieldText(config, 'imageUrl').trim();
  return {
    headline: fieldText(config, 'headline'),
    body: fieldText(config, 'body'),
    ctaLabel: fieldText(config, 'ctaLabel'),
    imageUrl: imageUrl || undefined,
    imageAlt: imageUrl ? fieldText(config, 'imageAlt') : undefined,
  };
}

/**
 * `controlPercent` and `targetEventName` are editable only while the wizard is still an
 * unpublished draft — the same condition `assertMeasurementUnchanged` applies server-side.
 * `publishedAt` survives an archive, so a wizard that was ever live stays frozen.
 */
export function isMeasurementFrozen(draft: WizardDraft): boolean {
  return !(draft.status === 'DRAFT' && !draft.publishedAt);
}

/** An app path with one leading slash, or an absolute https URL. Nothing else leaves safely. */
function validHref(value: string): boolean {
  if (value.startsWith('//')) return false;
  if (value.startsWith('/')) return true;
  if (!value.startsWith('https://')) return false;
  try {
    return Boolean(new URL(value).hostname);
  } catch {
    return false;
  }
}

function stepProblem(step: StepDraft): string | null {
  const label = `Step ${step.position}`;
  if (!fieldText(step.config, 'headline').trim()) return `${label} needs a headline.`;
  if (!fieldText(step.config, 'body').trim()) return `${label} needs body copy.`;
  if (!fieldText(step.config, 'ctaLabel').trim()) return `${label} needs a button label.`;
  const imageUrl = fieldText(step.config, 'imageUrl').trim();
  if (imageUrl && !validHostedUrl(imageUrl)) {
    return `${label}: the image URL must be an https:// address to an already-hosted image.`;
  }
  if (imageUrl && !fieldText(step.config, 'imageAlt').trim()) {
    return `${label} needs alt text describing its image.`;
  }
  return null;
}

/**
 * Every rule the API would reject, stated here instead — a teammate should not discover the
 * at-least-one-step rule or the 0-50 holdout range from a server error.
 */
export function draftProblem(draft: WizardDraft): string | null {
  const slug = draft.slug.trim();
  if (!draft.id) {
    if (!slug) return 'A slug is required, and this is the only screen where it can be set.';
    if (!SLUG_PATTERN.test(slug)) return 'The slug must be lowercase words joined by single hyphens.';
  }
  if (!draft.name.trim()) return 'A name is required.';
  if (!draft.targetEventName) return 'Choose the action this wizard should drive.';

  const percent = draft.controlPercent.trim() === '' ? NaN : Number(draft.controlPercent);
  if (!Number.isInteger(percent) || percent < 0 || percent > MAX_CONTROL_PERCENT) {
    return `The held-out audience must be a whole number from 0 to ${MAX_CONTROL_PERCENT}.`;
  }

  const handoff = draft.handoffHref.trim();
  if (handoff && !validHref(handoff)) {
    return 'The hand-off must be an app path starting with / or an absolute https:// address.';
  }

  if (draft.steps.length < 1) return 'A wizard needs at least one step.';
  return draft.steps.reduce<string | null>((memo, step) => memo ?? stepProblem(step), null);
}

/** Trim every authored string: the API treats a blank optional field as absent, not empty. */
function trimmedConfig(config: Record<string, unknown>): Record<string, unknown> {
  return Object.keys(config).reduce<Record<string, unknown>>((memo, key) => {
    const value = config[key];
    memo[key] = typeof value === 'string' ? value.trim() : value;
    return memo;
  }, {});
}

/**
 * `slug` is sent on every save because the input requires it, and is ignored server-side on
 * update — which is what lets a link a campaign already sent survive an edit.
 */
export function toUpsertInput(draft: WizardDraft) {
  return {
    id: draft.id,
    slug: draft.slug.trim(),
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    controlPercent: Number(draft.controlPercent) || 0,
    targetEventName: draft.targetEventName,
    handoffHref: draft.handoffHref.trim() || null,
    steps: draft.steps.map((step) => ({
      stepCode: step.stepCode,
      position: step.position,
      config: trimmedConfig(step.config),
    })),
  };
}

/** What the list copies: the parameter, not a link, because a wizard is not tied to a page. */
export function wizardParameter(slug: string): string {
  return `?wizard=${slug || '…'}`;
}

/** The scratch link builder's one computation. Nothing here is ever stored. */
export function buildWizardLink(url: string, slug: string): string {
  const trimmed = url.trim();
  if (!trimmed || !slug) return '';
  try {
    const parsed = new URL(trimmed);
    parsed.searchParams.set('wizard', slug);
    return parsed.toString();
  } catch {
    return `${trimmed}${trimmed.includes('?') ? '&' : '?'}wizard=${slug}`;
  }
}
